import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { AuditTrailComponent } from '@components/audit-trail/audit-trail.component';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { AuditTrailItem, ByProductItem, LocaleValue, PartnerType, PriceUnit, Pricing, ProductType, UpdatePriceRequest } from '@hjc/masterdata-types';
import { AuditTrailService } from '@services/audit-trail.service';
import { NotificationService } from '@services/notification.service';
import { PricingService } from '@services/pricing.service';
import { ProductsService } from '@services/products.service';
import { omit } from 'lodash-es';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedTranslationModule } from '@shared/translation.module';
import { InfoBlockComponent } from '@components/info/info-block/info-block.component';

@Component({
  selector: 'md-price-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    DatePipe,
    PageHeaderComponent,
    InfoBlockComponent,
    AuditTrailComponent,
    TranslateModule,
    SharedTranslationModule
  ],
  templateUrl: './price-details.component.html',
  styleUrl: './price-details.component.scss'
})
export class PriceDetailsComponent implements OnInit {
  // Inject services
  private route = inject(ActivatedRoute);
  private pricingService = inject(PricingService);
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);
  private auditTrailService = inject(AuditTrailService);
  private productsService = inject(ProductsService);
  private translate = inject(TranslateService);

  // State
  pricingData = signal<Pricing | undefined>(undefined);
  auditTrail = signal<AuditTrailItem[]>([]);
  isLoading = signal<boolean>(false);
  isEditing = signal<boolean>(false);
  currentLocale: LocaleValue = 'fi';
  displayedColumns: string[] = ['value', 'validFrom'];

  // Products data
  byProducts = signal<ByProductItem[]>([]);

  // Forms
  priceForm: FormGroup;
  newRateForm: FormGroup;
  pendingRates = signal<{ rate: number; validFrom: string }[]>([]);

  // Use the pricing service measures directly
  get measures() {
    return this.pricingService.measures;
  }

  // Options for dropdowns
  get priceUnits(): PriceUnit[] {
    return this.measures.map(m => m.id as PriceUnit);
  }

  productTypes: ProductType[] = ['byProduct', 'endProduct'];
  partnerTypes: PartnerType[] = ['slaughterhouse', 'haulier'];

  constructor() {
    this.priceForm = this.fb.group({
      id: [''],
      nameEn: [''],
      nameFi: [''],
      nameSv: [''],
      unit: [''],
      productType: [''],
      productId: [''],
      partnerType: [''],
      partnerId: ['']
    });

    // Form for adding new rates
    this.newRateForm = this.fb.group({
      rate: ['', [Validators.required, Validators.min(0)]],
      validFrom: [new Date(), Validators.required]
    });
  }

  ngOnInit(): void {
    // Get the ID from the route parameters and load data
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadPriceData(id);
      this.loadAuditTrail(id);
      this.loadByProducts();
    } else {
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.INVALID_PRICE_ID'));
    }

    // Setup form control value change listeners
    this.setupFormListeners();
  }

  async loadPriceData(id: string) {
    this.isLoading.set(true);

    try {
      const response = await this.pricingService.getPriceById(id);
      if (response.data) {
        this.pricingData.set(response.data);
        this.initializeForm();
      } else {
        this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.PRICE_INFO_NOT_FOUND'));
      }
    } catch (error) {
      console.error('Error loading price data:', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.PRICE_LOAD_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadAuditTrail(id: string): Promise<void> {
    try {
      const response = await this.auditTrailService.getAuditTrail<AuditTrailItem>('price', id);
      this.auditTrail.set(response?.data || []);
    } catch (error) {
      console.error('Error loading audit trail:', error);
      this.notificationService.error(this.translate.instant('AUDIT_TRAIL.LOAD_ERROR'));
    }
  }

  private async loadByProducts(): Promise<void> {
    try {
      const response = await this.productsService.getByProducts();
      if (response.data) {
        this.byProducts.set(response.data);
      }
    } catch (error) {
      console.error('Error loading byproducts:', error);
      this.notificationService.error(this.translate.instant('LOAD_ERROR'));
    }
  }

  private initializeForm() {
    const data = this.pricingData();
    if (data) {
      this.priceForm.patchValue({
        id: data.id,
        nameEn: data.name.en,
        nameFi: data.name.fi,
        nameSv: data.name.sv,
        unit: data.unit,
        productType: data.productType,
        productId: data.productId,
        partnerType: data.partnerType,
        partnerId: data.partnerId
      });
    }
  }

  private setupFormListeners() {
    // Listen for changes to the productId control
    this.priceForm.get('productId')?.valueChanges.subscribe(productId => {
      if (productId) {
        this.updateFormWithProductDetails(productId);
      }
    });
  }

  private updateFormWithProductDetails(productId: string) {
    const selectedProduct = this.byProducts().find(p => p.id === productId);
    if (selectedProduct && this.isEditing()) {
      // Update name fields with the product name automatically
      // These fields aren't editable in the UI but will be submitted with the form
      this.priceForm.patchValue({
        nameFi: selectedProduct.name.fi,
        nameEn: selectedProduct.name.en,
        nameSv: selectedProduct.name.sv
      });
    }
  }

  enableEditing() {
    this.isEditing.set(true);
    this.pendingRates.set([]);
  }

  cancelEditing() {
    this.isEditing.set(false);
    this.pendingRates.set([]);
    this.initializeForm();
  }

  addNewRate() {
    if (this.newRateForm.valid) {
      const rateValue = this.newRateForm.value.rate;
      const validFromDate = this.newRateForm.value.validFrom;

      // Add the new rate to pending rates
      this.pendingRates.update(rates => [
        ...rates,
        {
          rate: rateValue,
          validFrom: new Date(validFromDate).toISOString()
        }
      ]);

      // Reset the form for next entry
      this.newRateForm.reset({
        rate: '',
        validFrom: new Date()
      });
    }
  }

  removeRate(index: number) {
    this.pendingRates.update(rates => rates.filter((_, i) => i !== index));
  }

  async saveChanges() {
    if (this.priceForm.valid && this.pricingData()) {
      const formData = this.priceForm.value;
      const originalData = this.pricingData()!;

      // Create any new rates to add to the existing ones
      const newRates = this.pendingRates().map(rate => ({
        rate: rate.rate,
        validFrom: rate.validFrom
      }));

      // Update the data with form values, preserving the partner information
      const updatedData: UpdatePriceRequest = {
        ...omit(originalData, 'allRates'),
        name: {
          en: formData.nameEn,
          fi: formData.nameFi,
          sv: formData.nameSv
        },
        unit: formData.unit,
        productType: formData.productType,
        productId: formData.productId,
        partnerType: originalData.partnerType,
        partnerId: originalData.partnerId,
        rates: newRates.length > 0 ? newRates : originalData.allRates
      };

      try {
        this.isLoading.set(true);

        // Call the API to update the price
        const response = await this.pricingService.updatePrice(updatedData, formData.id);

        // Update the local state with the response
        this.pricingData.set(response.data);
        this.pendingRates.set([]);
        this.isEditing.set(false);
        this.notificationService.success(this.translate.instant('PRICE_DETAILS.SAVE_SUCCESS'));
      } catch (error) {
        console.error('Error saving price data:', error);
        this.notificationService.error(this.translate.instant('PRICE_DETAILS.SAVE_ERROR'));
      } finally {
        this.isLoading.set(false);
      }
    }
  }

  get data() {
    return this.pricingData;
  }

  // Helper method to get the display name for a unit
  getUnitDisplayName(unitId: string): string {
    const unit = this.measures.find(m => m.id === unitId);
    return unit ? unit.name : unitId;
  }

  // Helper method to get product name from ID
  getProductName(productId: string): string {
    const product = this.byProducts().find(p => p.id === productId);
    return product ? product.name[this.currentLocale] : productId;
  }
}
